import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@roger/ui-components';
import { WOUND_TYPES } from '../../constants/wounds';
import type { WoundResponse } from '../../services/api';
import WoundBodyMap, { type BodyMapPoint } from './WoundBodyMap';
import WoundTypeCombobox from './WoundTypeCombobox';
import { toDateStr } from './woundUtils';
import { parseISOToLocalDate } from '../../utils/dateUtils';

const DISCONTINUE_REASONS = [
  { value: 'wound_healed', label: 'Wound Healed' },
  { value: 'change_in_care', label: 'Change in Care' },
  { value: 'patient_refusal', label: 'Patient Refusal' },
  { value: 'other', label: 'Other' },
];

interface WoundFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingWound: WoundResponse | null;
  canDiscontinue?: boolean;
  onSave: (data: {
    location: string;
    wound_type: string;
    onset_date: string | null;
    body_map_point?: { x: number; y: number } | null;
    discontinued_date?: string | null;
    discontinued_reason?: string | null;
    discontinued_comments?: string | null;
  }) => Promise<void>;
}

const KNOWN_VALUES = new Set(WOUND_TYPES.map(t => t.value));

const WoundFormDialog: React.FC<WoundFormDialogProps> = ({
  open,
  onOpenChange,
  editingWound,
  canDiscontinue = false,
  onSave,
}) => {
  const [location, setLocation] = useState('');
  const [woundType, setWoundType] = useState('');
  const [otherDescription, setOtherDescription] = useState('');
  const [onsetDate, setOnsetDate] = useState<Date | undefined>(undefined);
  const [presentOnAdmission, setPresentOnAdmission] = useState(false);
  const [bodyMapPoint, setBodyMapPoint] = useState<BodyMapPoint | null>(null);
  const [discontinuedDate, setDiscontinuedDate] = useState<Date | undefined>(
    undefined
  );
  const [discontinuedReason, setDiscontinuedReason] = useState('');
  const [discontinuedReasonOther, setDiscontinuedReasonOther] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editingWound) {
        setLocation(editingWound.location);
        if (KNOWN_VALUES.has(editingWound.wound_type)) {
          setWoundType(editingWound.wound_type);
          setOtherDescription('');
        } else {
          setWoundType('other');
          setOtherDescription(editingWound.wound_type);
        }
        if (editingWound.onset_date) {
          setOnsetDate(parseISOToLocalDate(editingWound.onset_date));
          setPresentOnAdmission(false);
        } else {
          setOnsetDate(undefined);
          setPresentOnAdmission(true);
        }
        const bmp = (
          editingWound as WoundResponse & {
            body_map_point?: { x: number; y: number } | null;
          }
        ).body_map_point;
        setBodyMapPoint(bmp ?? null);
        setDiscontinuedDate(
          editingWound.discontinued_date
            ? parseISOToLocalDate(editingWound.discontinued_date)
            : undefined
        );
        const storedReason =
          (
            editingWound as WoundResponse & {
              discontinued_reason?: string | null;
            }
          ).discontinued_reason ?? '';
        if (storedReason === 'other') {
          setDiscontinuedReason('other');
          setDiscontinuedReasonOther(
            (
              editingWound as WoundResponse & {
                discontinued_comments?: string | null;
              }
            ).discontinued_comments ?? ''
          );
        } else {
          setDiscontinuedReason(storedReason);
          setDiscontinuedReasonOther('');
        }
      } else {
        setLocation('');
        setWoundType('');
        setOtherDescription('');
        setOnsetDate(undefined);
        setPresentOnAdmission(false);
        setBodyMapPoint(null);
        setDiscontinuedDate(undefined);
        setDiscontinuedReason('');
        setDiscontinuedReasonOther('');
      }
    }
  }, [open, editingWound]);

  const handleWoundTypeChange = (val: string) => {
    setWoundType(val);
    if (val !== 'other') setOtherDescription('');
  };

  const effectiveWoundType =
    woundType === 'other' ? otherDescription.trim() : woundType;

  const discontinuedValid =
    !discontinuedDate ||
    (!!discontinuedReason &&
      (discontinuedReason !== 'other' ||
        discontinuedReasonOther.trim().length > 0));

  const isValid =
    location.trim().length > 0 &&
    woundType.length > 0 &&
    (woundType !== 'other' || otherDescription.trim().length > 0) &&
    discontinuedValid;

  const handleSave = async () => {
    if (!isValid) return;
    setIsSaving(true);
    try {
      await onSave({
        location: location.trim(),
        wound_type: effectiveWoundType,
        onset_date: presentOnAdmission ? null : toDateStr(onsetDate),
        body_map_point: bodyMapPoint,
        ...(editingWound
          ? {
              discontinued_date: toDateStr(discontinuedDate),
              discontinued_reason: discontinuedReason || null,
              discontinued_comments:
                discontinuedReason === 'other'
                  ? discontinuedReasonOther.trim() || null
                  : null,
            }
          : {}),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{editingWound ? 'Edit Wound' : 'Add Wound'}</DialogTitle>
        </DialogHeader>

        <div className='grid grid-cols-2 gap-6 py-2'>
          {/* Left column: fields */}
          <div className='space-y-4'>
            {/* Location */}
            <div className='space-y-1.5'>
              <Label>
                Location <span className='text-destructive'>*</span>
              </Label>
              <Input
                placeholder='e.g. Left heel, Sacrum'
                value={location}
                onChange={e => setLocation(e.target.value)}
              />
            </div>

            {/* Wound Type */}
            <div className='space-y-1.5'>
              <Label>
                Wound Type <span className='text-destructive'>*</span>
              </Label>
              <WoundTypeCombobox
                value={woundType}
                onValueChange={handleWoundTypeChange}
                onAddCustomType={text => {
                  setWoundType('other');
                  setOtherDescription(text);
                }}
              />
              {woundType === 'other' && (
                <Input
                  autoFocus
                  placeholder='Describe the wound type...'
                  value={otherDescription}
                  onChange={e => setOtherDescription(e.target.value)}
                />
              )}
            </div>

            {/* Onset Date */}
            <div className='space-y-1.5'>
              <Label>Onset Date</Label>
              <DatePicker
                date={onsetDate}
                onDateChange={setOnsetDate}
                placeholder='Select date'
                datePickerSize='default'
                disabled={presentOnAdmission}
              />
              <div className='flex items-center gap-2 mt-1'>
                <Checkbox
                  id='present-on-admission'
                  checked={presentOnAdmission}
                  onCheckedChange={checked => {
                    setPresentOnAdmission(!!checked);
                    if (checked) setOnsetDate(undefined);
                  }}
                />
                <label
                  htmlFor='present-on-admission'
                  className='text-sm text-muted-foreground cursor-pointer'
                >
                  Present upon admission
                </label>
              </div>
            </div>

            {/* Discontinued — edit mode + visit context only */}
            {editingWound && canDiscontinue && (
              <>
                <Separator />
                <div className='space-y-3'>
                  <Label className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
                    Discontinuation
                  </Label>
                  <div className='space-y-1.5'>
                    <Label>Date Discontinued</Label>
                    <DatePicker
                      date={discontinuedDate}
                      onDateChange={d => {
                        setDiscontinuedDate(d);
                        if (!d) {
                          setDiscontinuedReason('');
                          setDiscontinuedReasonOther('');
                        }
                      }}
                      placeholder='Select date'
                      datePickerSize='default'
                      showClearButton
                    />
                  </div>
                  {discontinuedDate && (
                    <>
                      <div className='space-y-1.5'>
                        <Label>Reason</Label>
                        <Select
                          value={discontinuedReason}
                          onValueChange={v => {
                            setDiscontinuedReason(v);
                            if (v !== 'other') setDiscontinuedReasonOther('');
                          }}
                        >
                          <SelectTrigger selectSize='sm'>
                            <SelectValue placeholder='Select reason...' />
                          </SelectTrigger>
                          <SelectContent>
                            {DISCONTINUE_REASONS.map(r => (
                              <SelectItem key={r.value} value={r.value}>
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {discontinuedReason === 'other' && (
                        <div className='space-y-1.5'>
                          <Label>Please specify</Label>
                          <Input
                            placeholder='Describe reason...'
                            value={discontinuedReasonOther}
                            onChange={e =>
                              setDiscontinuedReasonOther(e.target.value)
                            }
                            inputSize='sm'
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right column: body map */}
          <div className='space-y-1.5'>
            <Label>Body Location</Label>
            <WoundBodyMap value={bodyMapPoint} onChange={setBodyMapPoint} />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid || isSaving}>
            {isSaving && <Loader2 className='h-4 w-4 mr-1 animate-spin' />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WoundFormDialog;
